"use client"

import { useState, useEffect } from "react"
import { ArrowRight, Apple, Zap, Shield, Terminal, CheckCircle, Download, Sparkles, Cpu, HardDrive } from "lucide-react"
import { Button } from "@/components/ui/button"

export default function MacOSTahoePage() {
  const [loaded, setLoaded] = useState(false)
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set())

  useEffect(() => {
    setLoaded(true)
    window.scrollTo(0, 0)

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id))
          }
        })
      },
      { threshold: 0.1 },
    )

    const sections = document.querySelectorAll("section[id]")
    sections.forEach((section) => observer.observe(section))

    return () => observer.disconnect()
  }, [])

  return (
    <div className="flex min-h-screen flex-col bg-[#1a1c1e] text-white overflow-hidden">
      <main className="flex-1">
        {/* Hero Section */}
        <section
          id="hero"
          className={`relative w-full py-16 md:py-24 lg:py-32 bg-gradient-to-br from-[#1a1c1e] via-[#252729] to-[#1a1c1e] transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
        >
          {/* Animated background */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#e57312]/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
          </div>

          <div className="container px-4 md:px-6 relative z-10">
            <div className="grid gap-8 lg:grid-cols-2 lg:gap-16 items-center">
              <div className="flex flex-col justify-center space-y-6">
                <div className="space-y-4">
                  <div className="inline-flex items-center rounded-full border border-purple-500/30 bg-purple-500/10 backdrop-blur-sm px-4 py-2 text-sm text-purple-300 animate-fade-in">
                    <Sparkles className="mr-2 h-4 w-4 animate-pulse" />
                    <span className="font-medium">Experimental Release</span>
                  </div>
                  <h1 className="text-4xl font-bold tracking-tighter sm:text-6xl xl:text-7xl/none bg-gradient-to-r from-white via-purple-200 to-[#e57312] bg-clip-text text-transparent animate-fade-in-up delay-200">
                    macOS <span className="text-[#e57312]">Tahoe 26</span>
                  </h1>
                  <p className="max-w-[600px] text-xl text-gray-300 leading-relaxed animate-fade-in-up delay-400">
                    Experience the cutting-edge of Apple's operating system in your Proxmox environment. macOS Tahoe 26
                    brings breakthrough performance and innovative features to your virtualization infrastructure.
                  </p>
                </div>

                {/* Key Features */}
                <div className="grid grid-cols-2 gap-4 py-4 animate-fade-in-up delay-600">
                  <div className="flex items-center space-x-3 bg-[#252729]/50 backdrop-blur-sm rounded-lg p-3 border border-[#2c2e30]">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10">
                      <Apple className="h-5 w-5 text-purple-300" />
                    </div>
                    <div>
                      <div className="font-semibold text-white text-sm">macOS 26.0.1</div>
                      <div className="text-xs text-gray-400">Latest Build</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 bg-[#252729]/50 backdrop-blur-sm rounded-lg p-3 border border-[#2c2e30]">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#e57312]/10">
                      <HardDrive className="h-5 w-5 text-[#e57312]" />
                    </div>
                    <div>
                      <div className="font-semibold text-white text-sm">10.5 GB</div>
                      <div className="text-xs text-gray-400">Installer</div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-800">
                  <Button
                    size="lg"
                    className="group bg-gradient-to-r from-[#e57312] to-[#d4640f] hover:from-[#d4640f] hover:to-[#e57312] text-white shadow-lg hover:shadow-[#e57312]/25 transition-all duration-300 transform hover:scale-105"
                    onClick={() => (window.location.href = "/templates/purchase")}
                  >
                    Get Template Script
                    <Download className="ml-2 h-5 w-5 group-hover:translate-y-0.5 transition-transform" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-[#e57312]/50 text-[#e57312] hover:bg-[#e57312]/10 backdrop-blur-sm transition-all duration-300 bg-transparent"
                    onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
                  >
                    Learn More
                  </Button>
                </div>

                {/* Warning Badge */}
                <div className="flex items-start space-x-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 animate-fade-in-up delay-1000">
                  <Sparkles className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-200">
                    <strong>Experimental Release:</strong> macOS Tahoe 26 is currently in experimental stage. While
                    fully functional, some features may be unstable. We also offer a preinstalled version for easier
                    deployment.
                  </div>
                </div>
              </div>

              {/* Visual Element */}
              <div className="relative group animate-fade-in-up delay-1000">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-[#e57312]/20 to-purple-500/20 rounded-2xl blur-2xl group-hover:blur-3xl transition-all duration-500"></div>
                <div className="relative rounded-2xl border border-[#2c2e30]/50 bg-[#252729]/80 backdrop-blur-sm p-8 shadow-2xl overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-[#e57312]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                  {/* macOS Window Preview */}
                  <div className="relative">
                    <div className="bg-[#1a1c1e] rounded-xl border border-[#2c2e30] p-4 shadow-xl">
                      <div className="flex items-center space-x-2 mb-4">
                        <div className="h-3 w-3 rounded-full bg-red-500"></div>
                        <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                        <div className="h-3 w-3 rounded-full bg-green-500"></div>
                        <div className="flex-1 text-center text-xs text-gray-400">macOS Tahoe</div>
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-center justify-center py-12 bg-gradient-to-br from-purple-500/20 to-[#e57312]/20 rounded-lg">
                          <Apple className="h-24 w-24 text-white/80" />
                        </div>
                        <div className="space-y-2">
                          <div className="h-2 bg-[#e57312]/30 rounded-full w-3/4"></div>
                          <div className="h-2 bg-purple-500/30 rounded-full w-1/2"></div>
                          <div className="h-2 bg-[#e57312]/30 rounded-full w-5/6"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section
          id="features"
          className={`w-full py-16 md:py-24 bg-gradient-to-b from-[#252729] to-[#1a1c1e] transition-all duration-1000 ${visibleSections.has("features") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
        >
          <div className="container px-4 md:px-6">
            <div className="flex flex-col items-center justify-center space-y-4 text-center mb-16">
              <div className="space-y-4">
                <div className="inline-block rounded-lg bg-[#1a1c1e]/80 backdrop-blur-sm border border-[#e57312]/20 px-4 py-2 text-sm text-[#e57312] font-medium">
                  What's New
                </div>
                <h2 className="text-4xl font-bold tracking-tighter md:text-5xl/tight bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  Breakthrough <span className="text-[#e57312]">Features</span> in Tahoe
                </h2>
                <p className="max-w-[900px] text-xl text-gray-300 leading-relaxed">
                  macOS Tahoe 26 introduces powerful enhancements designed for modern computing.
                </p>
              </div>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  icon: Zap,
                  title: "Enhanced Performance",
                  description:
                    "Significant performance improvements with optimized resource management and faster app launching.",
                  gradient: "from-yellow-500/10 to-orange-500/10",
                  iconBg: "bg-yellow-500/10",
                  iconColor: "text-yellow-400",
                },
                {
                  icon: Shield,
                  title: "Advanced Security",
                  description:
                    "Next-generation security features with improved encryption and privacy controls for enterprise environments.",
                  gradient: "from-blue-500/10 to-cyan-500/10",
                  iconBg: "bg-blue-500/10",
                  iconColor: "text-blue-400",
                },
                {
                  icon: Cpu,
                  title: "AI Integration",
                  description:
                    "Built-in AI capabilities that enhance productivity and provide intelligent system optimizations.",
                  gradient: "from-purple-500/10 to-pink-500/10",
                  iconBg: "bg-purple-500/10",
                  iconColor: "text-purple-400",
                },
                {
                  icon: Terminal,
                  title: "Developer Tools",
                  description:
                    "Enhanced development environment with improved debugging tools and performance profiling.",
                  gradient: "from-green-500/10 to-emerald-500/10",
                  iconBg: "bg-green-500/10",
                  iconColor: "text-green-400",
                },
                {
                  icon: Apple,
                  title: "Modern UI/UX",
                  description: "Refreshed interface with smoother animations and improved accessibility features.",
                  gradient: "from-[#e57312]/10 to-orange-500/10",
                  iconBg: "bg-[#e57312]/10",
                  iconColor: "text-[#e57312]",
                },
                {
                  icon: HardDrive,
                  title: "Storage Efficiency",
                  description:
                    "Advanced compression and intelligent file management for optimized storage utilization.",
                  gradient: "from-indigo-500/10 to-blue-500/10",
                  iconBg: "bg-indigo-500/10",
                  iconColor: "text-indigo-400",
                },
              ].map((feature, index) => (
                <div
                  key={index}
                  className={`group relative rounded-2xl border border-[#2c2e30]/50 bg-gradient-to-br ${feature.gradient} backdrop-blur-sm p-6 transition-all duration-500 hover:border-[#e57312]/50 hover:shadow-2xl hover:shadow-[#e57312]/10 hover:-translate-y-2`}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                  <div
                    className={`flex h-14 w-14 items-center justify-center rounded-xl ${feature.iconBg} ${feature.iconColor} mb-4 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <feature.icon className="h-7 w-7" />
                  </div>

                  <h3 className="text-xl font-bold text-white group-hover:text-[#e57312] transition-colors mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Version Options Section */}
        <section
          id="versions"
          className={`w-full py-16 md:py-24 bg-gradient-to-b from-[#1a1c1e] to-[#252729] transition-all duration-1000 ${visibleSections.has("versions") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
        >
          <div className="container px-4 md:px-6">
            <div className="flex flex-col items-center justify-center space-y-4 text-center mb-12">
              <div className="space-y-4">
                <div className="inline-block rounded-lg bg-[#252729]/80 backdrop-blur-sm border border-[#e57312]/20 px-4 py-2 text-sm text-[#e57312] font-medium">
                  Choose Your Version
                </div>
                <h2 className="text-4xl font-bold tracking-tighter md:text-5xl/tight bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  Two Deployment <span className="text-[#e57312]">Options</span>
                </h2>
                <p className="max-w-[900px] text-xl text-gray-300 leading-relaxed">
                  Select the version that best fits your deployment needs and experience level.
                </p>
              </div>
            </div>

            <div className="grid gap-8 md:grid-cols-2 max-w-5xl mx-auto">
              {/* Experimental Version */}
              <div className="group relative rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-[#252729] backdrop-blur-sm p-8 transition-all duration-500 hover:border-purple-500/50 hover:shadow-2xl hover:shadow-purple-500/10">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-purple-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                  For Advanced Users
                </div>
                <div className="space-y-6">
                  <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-purple-500/10 text-purple-300">
                    <Terminal className="h-8 w-8" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Experimental</h3>
                    <p className="text-gray-300 mb-4">Clean installer requiring manual setup</p>
                    <div className="flex items-baseline mb-4">
                      <span className="text-3xl font-bold text-purple-300">10.5 GB</span>
                      <span className="ml-2 text-gray-400">Download size</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-300 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">Fresh installation experience</span>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-300 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">Customize from the ground up</span>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-300 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">Smaller download footprint</span>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-300 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">Manual configuration required</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preinstalled Version */}
              <div className="group relative rounded-2xl border border-[#e57312]/50 bg-gradient-to-br from-[#e57312]/10 to-[#252729] backdrop-blur-sm p-8 transition-all duration-500 hover:border-[#e57312] hover:shadow-2xl hover:shadow-[#e57312]/10">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-[#e57312] text-white text-xs px-3 py-1 rounded-full font-medium">
                  Recommended
                </div>
                <div className="space-y-6">
                  <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-[#e57312]/10 text-[#e57312]">
                    <Zap className="h-8 w-8" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Preinstalled</h3>
                    <p className="text-gray-300 mb-4">Ready-to-use with preconfigured settings</p>
                    <div className="flex items-baseline mb-4">
                      <span className="text-3xl font-bold text-[#e57312]">12.3 GB</span>
                      <span className="ml-2 text-gray-400">Download size</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-[#e57312] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">Boot directly into macOS</span>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-[#e57312] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">Pre-optimized settings</span>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-[#e57312] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">Minimal setup required</span>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-[#e57312] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">Perfect for quick testing</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 text-center">
              <p className="text-gray-400 mb-6">Both versions included in the Template Script</p>
              <Button
                size="lg"
                className="bg-gradient-to-r from-[#e57312] to-[#d4640f] hover:from-[#d4640f] hover:to-[#e57312] text-white shadow-lg hover:shadow-[#e57312]/25 transition-all duration-300 transform hover:scale-105"
                onClick={() => (window.location.href = "/templates/purchase")}
              >
                Get Template Script
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section
          id="cta"
          className={`w-full py-16 md:py-24 bg-gradient-to-b from-[#252729] to-[#1a1c1e] transition-all duration-1000 ${visibleSections.has("cta") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
        >
          <div className="container px-4 md:px-6">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-[#e57312]/20 to-purple-500/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
              <div className="relative bg-gradient-to-br from-[#252729]/80 to-[#1a1c1e]/80 backdrop-blur-sm rounded-3xl border border-[#2c2e30]/50 p-8 md:p-16 shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-br from-[#e57312]/5 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                <div className="text-center relative z-10">
                  <div className="inline-flex items-center rounded-full border border-purple-500/30 bg-purple-500/10 backdrop-blur-sm px-4 py-2 text-sm text-purple-300 mb-6">
                    <Sparkles className="mr-2 h-4 w-4" />
                    <span className="font-medium">Limited Experimental Access</span>
                  </div>
                  <h2 className="text-4xl font-bold tracking-tighter md:text-5xl/tight mb-6 bg-gradient-to-r from-white via-purple-200 to-[#e57312] bg-clip-text text-transparent">
                    Ready to Experience <span className="text-[#e57312]">macOS Tahoe</span>?
                  </h2>
                  <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl mx-auto">
                    Get instant access to macOS Tahoe 26 and all other templates in our library. Deploy cutting-edge
                    virtualization solutions with our easy-to-use script.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-[#e57312] to-[#d4640f] hover:from-[#d4640f] hover:to-[#e57312] text-white shadow-lg hover:shadow-[#e57312]/25 transition-all duration-300 transform hover:scale-105"
                      onClick={() => (window.location.href = "/templates/purchase")}
                    >
                      Get Template Script - £7.99
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-[#e57312]/50 text-[#e57312] hover:bg-[#e57312]/10 backdrop-blur-sm transition-all duration-300 bg-transparent"
                      onClick={() => (window.location.href = "/templates")}
                    >
                      View All Templates
                    </Button>
                  </div>

                  <div className="mt-8 pt-8 border-t border-[#2c2e30]">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left max-w-4xl mx-auto">
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-[#e57312] flex-shrink-0 mt-1" />
                        <div>
                          <div className="font-semibold text-white">6 Month Access</div>
                          <div className="text-sm text-gray-400">Full template library</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-[#e57312] flex-shrink-0 mt-1" />
                        <div>
                          <div className="font-semibold text-white">All Templates</div>
                          <div className="text-sm text-gray-400">Windows, macOS, Linux</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-[#e57312] flex-shrink-0 mt-1" />
                        <div>
                          <div className="font-semibold text-white">Email Support</div>
                          <div className="text-sm text-gray-400">Technical assistance</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fade-in-up {
          from { 
            opacity: 0; 
            transform: translateY(30px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out forwards;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
        
        .delay-200 { animation-delay: 200ms; }
        .delay-400 { animation-delay: 400ms; }
        .delay-600 { animation-delay: 600ms; }
        .delay-800 { animation-delay: 800ms; }
        .delay-1000 { animation-delay: 1000ms; }
      `}</style>
    </div>
  )
}
